/**
 * Users Redux Selectors
 */

import { createSelector } from '@reduxjs/toolkit'
import type { UsersState } from './users.slice'
import type { User } from '../domain/types'

interface RootState {
  users: UsersState
}

// Base selectors
export const selectUsersState = (state: RootState) => state.users
export const selectUsers = (state: RootState) => state.users.items
export const selectSelectedUser = (state: RootState) => state.users.selectedUser
export const selectUserStats = (state: RootState) => state.users.stats
export const selectUsersFilter = (state: RootState) => state.users.filter
export const selectUsersPagination = (state: RootState) => state.users.pagination
export const selectUsersLoading = (state: RootState) => state.users.loading
export const selectUsersError = (state: RootState) => state.users.error
export const selectCreateUserStatus = (state: RootState) => state.users.createUserStatus
export const selectCreateUserError = (state: RootState) => state.users.createUserError
export const selectUpdateUserStatus = (state: RootState) => state.users.updateUserStatus
export const selectUpdateUserError = (state: RootState) => state.users.updateUserError

// Users are now filtered by backend, just return items
export const selectFilteredUsers = selectUsers

// Stats selectors
export const selectActiveUsersCount = createSelector([selectUsers], (users) =>
  users.filter((u) => u.status === 'active' && !u.blocked).length
)

export const selectBlockedUsersCount = createSelector([selectUsers], (users) =>
  users.filter((u) => u.blocked).length
)

export const selectInvitedUsersCount = createSelector([selectUsers], (users) =>
  users.filter((u) => u.status === 'invited').length
)

// Users by tenant
export const selectUsersByTenant = createSelector([selectUsers], (users) => {
  const byTenant: Record<string, User[]> = {}
  users.forEach((user) => {
    const tenantId = user.tenant?.id ?? 'no-tenant'
    if (!byTenant[tenantId]) {
      byTenant[tenantId] = []
    }
    byTenant[tenantId].push(user)
  })
  return byTenant
})

// Users by role
export const selectUsersByRole = createSelector([selectUsers], (users) => {
  const byRole: Record<string, User[]> = {}
  users.forEach((user) => {
    const role = user.strapiRole?.type ?? user.role
    if (!byRole[role]) {
      byRole[role] = []
    }
    byRole[role].push(user)
  })
  return byRole
})

// Select user by ID
export const selectUserById = (userId: string) =>
  createSelector([selectUsers], (users) => users.find((u) => u.id === userId) ?? null)

// Computed stats from items (for current page)
export const selectComputedStats = createSelector(
  [selectUsers, selectUsersPagination, selectUserStats],
  (users, pagination, storedStats) => {
    // If we have stored stats from backend, use those for totals
    if (storedStats.total > 0) {
      return storedStats
    }

    // Otherwise compute from current page data
    const stats = {
      active: 0,
      inactive: 0,
      invited: 0,
      blocked: 0,
      total: pagination.total,
      byRole: {} as Record<string, number>,
    }

    users.forEach((user) => {
      // Count by status
      if (user.blocked) {
        stats.blocked += 1
      } else if (user.status === 'active') {
        stats.active += 1
      } else if (user.status === 'inactive') {
        stats.inactive += 1
      } else if (user.status === 'invited') {
        stats.invited += 1
      }

      // Count by role
      const role = user.strapiRole?.type ?? user.role
      stats.byRole[role] = (stats.byRole[role] ?? 0) + 1
    })

    return stats
  }
)

// Pagination convenience selectors
export const selectCurrentPage = createSelector(
  [selectUsersPagination],
  (pagination) => pagination.page
)

export const selectPageSize = createSelector(
  [selectUsersPagination],
  (pagination) => pagination.pageSize
)

export const selectTotalPages = createSelector(
  [selectUsersPagination],
  (pagination) => pagination.pageCount
)

export const selectTotalItems = createSelector(
  [selectUsersPagination],
  (pagination) => pagination.total
)
